import { type CartPageState, type CartState, type CheckoutPageStore } from "../Cart/redux/types";
import { type GlobalMessagesStore } from "./globalMessages/types";
import { type ProductsStore } from "./products/types";
import { cartAmountsReducer } from "../AddToCart/redux/cartAmounts/reducer";
import { type ContractsStore } from "../Contracts/redux/types";
import { type NavigationRootState } from "../Header/redux/types";
import { srpReducer } from "../Search/redux/reducer";
import { type WishlistPageState } from "../Wishlist/redux/types";
import { applyMiddleware, combineReducers, createStore, type Reducer } from "redux";
import { cartPageReducer } from "../Cart/redux/reducers/cartPageReducer";
import { checkoutPageReducer } from "../Cart/redux/reducers/checkoutPageReducer";
import { cartReducer } from "../Cart/redux/reducers/cartReducer";
import { globalMessagesReducer } from "./globalMessages/reducer";
import { productsReducer } from "./products/reducer";
import { contractsReducer } from "../Contracts/redux/reducer";
import { wishlistReducer } from "../Wishlist/redux/wishlistReducer";
import { productDetailsPageReducer } from "../ProductDetails/redux/productDetailsReducer";
import { navigationReducer } from "../Header/redux/reducer";
import thunk, { type ThunkMiddleware } from "redux-thunk";
import { type AnyAction } from "./types";
import { type CartAmountsStore } from "../AddToCart/redux/cartAmounts/types";
import { type ProductDetailsPageState } from "../ProductDetails/redux/productDetailsTypes";
import { type SearchPageState } from "../Search/redux/types";
import { composeWithDevTools } from "redux-devtools-extension/developmentOnly";
import { type LinearConfiguratorState } from "../Configurators/Linear/redux/types";
import { linearConfiguratorReducer } from "../Configurators/Linear/redux/reducer";
import { type DictionaryLoadable } from "./loadableStore/types";
import { externalContentReducer } from "./externalContent/reducer";

import { productComparisonReducer } from "../ProductComparison/redux/reducer";
import { type ProductComparisonState } from "../ProductComparison/redux/types";
import { regionSelectionBannerReducer, type RegionSelectionBannerState } from "../RegionSelectionBanner/reducer";
import { HOUSING_SA_REDUCER } from "../HousingSelectionAssistant/redux/reducer";
import { type SAState } from "../HousingSelectionAssistant/redux/types";
import { imperialMetricDataReducer } from "../ImperialMetricData/redux/ImperialMetricDataReducer";
import { type ImperialMetricDataState } from "../ImperialMetricData/redux/ImperialMetricDataState";
import { notificationsReducer } from "../Notifications/redux/NotificationsReducer";
import { type NotificationsPageState } from "../Notifications/redux/types";
import { type SaPageState } from "~/BearingSelectionAssistant/redux/types";
import { saReducer } from "~/BearingSelectionAssistant/redux/reducer";
import { minicartReducer, type MinicartStore } from "~/Cart/components/Minicart/minicartRedux";
import { affiliateReducer, type AffiliateStore } from "~/feature/Affiliate/redux/reducer";
import { searchQueryReducer, type SearchState } from "~/Header/components/headerMenu/Autosuggest/redux/reducer";

export interface RootState {
  cart: CartState;
  cartPage: CartPageState;
  checkoutPage: CheckoutPageStore;
  cartAmounts: CartAmountsStore;
  contracts: ContractsStore;
  navigation: NavigationRootState;
  wishlistPage: WishlistPageState;
  pdp: ProductDetailsPageState;
  srp: SearchPageState;
  sa: SaPageState;
  globalMessages: GlobalMessagesStore;
  products: ProductsStore;
  linearConfigurator: LinearConfiguratorState;
  regionSelectionBanner: RegionSelectionBannerState;

  externalContent: DictionaryLoadable<string>;
  productComparison: ProductComparisonState;

  housingSA: SAState;
  imperialMetricData: ImperialMetricDataState;

  notifications: NotificationsPageState;
  minicart: MinicartStore;

  affiliate: AffiliateStore;

  search: SearchState;
}

export function createReducer(): Reducer<RootState> {
  return combineReducers({
    cartPage: cartPageReducer,
    checkoutPage: checkoutPageReducer,
    cart: cartReducer,
    globalMessages: globalMessagesReducer,
    products: productsReducer,
    cartAmounts: cartAmountsReducer,
    linearConfigurator: linearConfiguratorReducer,
    regionSelectionBanner: regionSelectionBannerReducer,

    contracts: contractsReducer,
    wishlistPage: wishlistReducer,

    srp: srpReducer,
    pdp: productDetailsPageReducer,
    sa: saReducer,
    navigation: navigationReducer,

    externalContent: externalContentReducer,
    productComparison: productComparisonReducer,
    housingSA: HOUSING_SA_REDUCER,
    imperialMetricData: imperialMetricDataReducer,

    notifications: notificationsReducer,

    minicart: minicartReducer,

    affiliate: affiliateReducer,

    search: searchQueryReducer,
  });
}

export const store = createStore(
  createReducer(),
  composeWithDevTools({ trace: true })(applyMiddleware(thunk as ThunkMiddleware<RootState, AnyAction>)),
);
